import { ADYEN_KEY } from 'utils/env';
import CheckoutModal from '@picsart/monetization/CheckoutModal';
import { CheckoutModalProvider } from '@picsart/monetization/CheckoutModalContext';

import { PaymentFlowType } from '@picsart/monetization/types/models/paymentFlowType';
import {
  getCheckoutModal,
  hideCheckoutModal,
  setCheckoutSuccess,
  setDownloadingSuccess,
  setPurchasedItems,
  showCheckoutModal
} from 'modules/checkoutModal';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, getUserKey, updateUser } from 'modules/user';
import { useCallback, useEffect, useMemo } from 'react';
import { loadUserPaidItems } from 'modules/oneTimePayment';
import { PremiumItemSource, RESOLUTIONS } from '../constants';
import analytics from 'utils/analytics';
import { download } from 'modules/downloading';
import { chargeShutterstock } from 'utils/apiWrapper';
import { getCurrentProjectId, saveProjectFile } from 'modules/projectFles';
import { PremiumData } from './header/OneTimePayAccordion';
import { CheckoutProps } from '@picsart/monetization/types/components/CheckoutModal/Checkout/types';
import { IAuthenticationProps } from '@picsart/rc/types/containerComponents/Authentication';

const removePurchasedItems = (
  premiumItems: PremiumData[],
  purchasedItems: PremiumData[]
): PremiumData[] | null | undefined => {
  if (!purchasedItems || !premiumItems) {
    return premiumItems;
  }

  return premiumItems.filter(
    premiumItem =>
      !purchasedItems.some(
        purchasedItem =>
          purchasedItem.id === premiumItem.id &&
          purchasedItem.source === premiumItem.source &&
          purchasedItem.type === premiumItem.type &&
          purchasedItem.src === premiumItem.src
      )
  );
};

export default () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const userKey = useSelector(getUserKey);
  const {
    isOpen,
    actionButton,
    premiumItems,
    settings,
    isSucceed: isPaymentSucceed,
    isDownloaded,
    purchasedItems
  } = useSelector(getCheckoutModal);
  const currentProjectId = useSelector(getCurrentProjectId);

  const setCheckoutModalOpenState = useCallback((state: boolean) => {
    if (state) {
      dispatch(showCheckoutModal(null, null));
    } else {
      dispatch(hideCheckoutModal());
    }
  }, []);

  useEffect(() => {
    if (settings !== null) {
      dispatch(saveProjectFile());
    }
  }, [settings]);

  const paymentSuccessHandler = useCallback(
    (flow?: any) => {
      if (flow === 'onetime') {
        const shutterstockItems = premiumItems
          .filter(item => item.source === PremiumItemSource.Shutterstock)
          .map(({ type, id }) => ({
            type,
            id
          }));
        chargeShutterstock(userKey, shutterstockItems);

        dispatch(setPurchasedItems(premiumItems));
        dispatch(loadUserPaidItems(premiumItems));
        dispatch(setCheckoutSuccess(true));
      }
    },
    [premiumItems, userKey, isPaymentSucceed]
  );

  const handleCompletePayment = useCallback(
    (flow?: 'onetime' | 'subscription') => {
      if (!isPaymentSucceed) {
        paymentSuccessHandler();
      }

      if (flow === 'subscription' && user && user.id && user.key) {
        dispatch(updateUser({ subscribed: true }));
      }

      if (settings) {
        const { width, height } = RESOLUTIONS.find(item => item.resolution === settings.resolution);

        analytics.exportPopup({
          action_type: 'download',
          ...settings.exportAnalyticsData
        });

        dispatch(setDownloadingSuccess(false));

        dispatch(
          download({
            width,
            height,
            namePrefix: settings.namePrefix,
            container: settings.container,
            resolution: settings.resolution
          })
        );
      }

      dispatch(hideCheckoutModal());
    },
    [updateUser, hideCheckoutModal, settings, isPaymentSucceed, paymentSuccessHandler]
  );

  const toolNames = (premiumItems ?? []).reduce((prev, curr) => {
    prev = {
      ...prev,
      [curr.type]: curr.id
    };
    return prev;
  }, {});

  const itemsToBuy = useMemo(
    () => (isDownloaded ? premiumItems : removePurchasedItems(premiumItems, purchasedItems)),
    [premiumItems, purchasedItems, isDownloaded]
  );
  const gtmData = {
    screen_id: 'popup',
    source: 'video_editor',
    tool_names: actionButton === 'pay_and_download' ? premiumItems : toolNames,
    action_button: actionButton
  };

  const checkoutProps = useMemo(
    () =>
      ({
        gtmData,
        adyenKey: ADYEN_KEY,
        premiumItems: actionButton === 'pay_and_download' ? itemsToBuy : null,
        entryPoint: '',
        callPaymentSuccessOn3dSecure: true,
        isSecondFreeTrialCheckEnabled: true,
        onCompletePaymentClick: handleCompletePayment,
        onPaymentSuccess: (flow: PaymentFlowType) => {
          if (flow === 'subscription') {
            handleCompletePayment();
          }
        },
        initialView:
          user?.subscription?.is_trial_used || actionButton === 'pay_and_download'
            ? 'form'
            : 'onboarding'
      } as CheckoutProps),
    [gtmData, handleCompletePayment]
  );
  const authProps = {
    isAppleRegion: true,
    view: 'signUp'
  } as IAuthenticationProps;

  return (
    currentProjectId && (
      <CheckoutModalProvider setIsOpen={setCheckoutModalOpenState} isOpen={isOpen}>
        <CheckoutModal
          checkoutProps={checkoutProps}
          authProps={authProps}
          onAlreadySubscribed={() => setCheckoutModalOpenState(false)}
        />
      </CheckoutModalProvider>
    )
  );
};
